import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import OtpInput from "react18-input-otp";
import {
  usePersistEstimationMutation,
  usePurchaseSolarEstimationMutation,
  useSubmitForApprovalMutation,
  useUpdateEstimationMutation,
} from "../../../../../redux/api/solar-estimation/solarEstimationAPI";
import {
  useLazyProfileQuery,
  useLazyValidateOTPQuery,
} from "../../../../../redux/api/user/userAPI";
import { selectAuth } from "../../../../../redux/features/auth/auth-slice";
import { selectCountry } from "../../../../../redux/features/user/user-slice";
import { IDataToSubmitForApproval } from "../../../../../screens/build-system/components/approved-system/approved-system";
import { EEstimationType } from "../../../enums/estimation-type";
import { OvstErrorCode } from "../../../enums/ovst-error-codes";
import { ESolarSolutionType } from "../../../enums/sola-solution-type";
import { ESolarSimLocalStorage } from "../../../enums/solar-sim-local-storage";
import { ETimePeriod } from "../../../enums/time-period";
import { UserRole } from "../../../enums/user-roles";
import { AppRoute } from "../../../interfaces/app-routes";
import { IHttpError } from "../../../interfaces/http-errror";
import EmailOtpVerificationFooter from "../../../shared-components/email-otp-verification-footer/email-otp-verification-footer";
import AppButton from "../../../ui-elements/buttons/app-button/app-button";
import SpinnerModal from "../../../ui-elements/spinner/spinner";
import { showErrorMessage, showSuccessMessage } from "../../../utils/toast";
import styles from "./email-otp-verification-form.module.scss";

interface IProps {
  email: string;
  whereTo: string;
}

const EmailOtpVerificationForm = (props: IProps) => {
  const navigate = useNavigate();
  const { email, whereTo } = props;
  const [otp, setOtp] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState<boolean>(true);
  const auth = useSelector(selectAuth);
  const country = useSelector(selectCountry);

  const handleChange = (value: string) => {
    setOtp(value);
    setEnteredOtp(value.length === 6 ? false : true);
  };

  const [triggerGetOTP, { isLoading: loadingGetOTP }] =
    useLazyValidateOTPQuery();
  const [getProfile, { isFetching: isGetProfileFetching }] =
    useLazyProfileQuery();
  const [triggerPersistEstimation, { isLoading: isLoadingPersistEstimation }] =
    usePersistEstimationMutation();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();
  const [submitForApproval, { isLoading: isSubmittingForApproval }] =
    useSubmitForApprovalMutation();
  const [
    triggerPurchaseSolarEstimation,
    { isLoading: isLoadingPurchaseSolarEstimation },
  ] = usePurchaseSolarEstimationMutation();

  const resetError = () => {
    setIsValid(true);
  };

  const onSubmit = () => {
    triggerGetOTP({ email: email, otp: otp })
      .unwrap()
      .then(() => {
        if (whereTo === "reset") {
          navigate(AppRoute.RESET_PASSWORD);
        } else if (whereTo === "dashboard/user-profile") {
          getProfile({ role: auth.userRole })
            .unwrap()
            .then(() => {
              navigate(AppRoute.USER_PROFILE);
              showSuccessMessage("Email updated successfully");
            });
        } else if (whereTo === "dashboard") {
          getProfile({ role: auth.userRole })
            .unwrap()
            .then(() => {
              navigate(AppRoute.DASHBOARD);
            });
        } else if (whereTo === "estimations") {
          getProfile({ role: auth.userRole })
            .unwrap()
            .then(() => {
              if (auth.userRole === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN) {
                navigate(AppRoute.ESTIMATIONS);
                return;
              }

              const unparsedDataToSubmitForApproval = localStorage.getItem(
                "dataToSubmitForApproval",
              );
              const isPurchase = localStorage.getItem("isPurchase");

              if (unparsedDataToSubmitForApproval) {
                const parsedDataToSubmitForApproval: IDataToSubmitForApproval =
                  JSON.parse(unparsedDataToSubmitForApproval);

                switch (parsedDataToSubmitForApproval.solarEstimationType) {
                  case EEstimationType.SIMPLIFIED:
                    triggerPersistEstimation({
                      estimationId: auth.token
                        ? parsedDataToSubmitForApproval.estimatorDetails
                            .estimationId
                        : "",
                      estimationToken: auth.token
                        ? ""
                        : parsedDataToSubmitForApproval.estimatorDetails
                            .estimationToken,
                      title: parsedDataToSubmitForApproval.title,
                      usages: parsedDataToSubmitForApproval.devices
                        .filter(
                          (device) =>
                            Number(device.amount) > 0 &&
                            Number(device.wattage) > 0 &&
                            Number(device.usage) > 0,
                        )
                        .map((device) => ({
                          name: device.deviceName,
                          type: device.deviceType,
                          amount: Number(device.amount),
                          wattage: Number(device.wattage),
                          usageInHours: Number(device.usage),
                          timePeriod: ETimePeriod.WEEKLY,
                        })),
                      isDraft: true,
                      countryCode: parsedDataToSubmitForApproval.country,
                    })
                      .unwrap()
                      .then((persistRes) => {
                        triggerUpdateEstimation({
                          estimationId: auth.token
                            ? parsedDataToSubmitForApproval.estimatorDetails
                                .estimationId
                            : "",
                          estimatorToken: auth.token
                            ? ""
                            : parsedDataToSubmitForApproval.estimatorDetails
                                .estimationToken,
                          title: parsedDataToSubmitForApproval.title,
                          estimationType:
                            parsedDataToSubmitForApproval.estimationType,
                          applicationType:
                            parsedDataToSubmitForApproval.estimationType ===
                            ESolarSolutionType.BOTH
                              ? parsedDataToSubmitForApproval.applicationType
                              : null,
                          utilityOptionType:
                            parsedDataToSubmitForApproval.estimationType ===
                            ESolarSolutionType.BOTH
                              ? parsedDataToSubmitForApproval.utilityOptionType
                              : null,
                          solarEstimationType: EEstimationType.SIMPLIFIED,
                          systemType: parsedDataToSubmitForApproval.systemType,
                          inverterPhaseType:
                            parsedDataToSubmitForApproval.inverterPhaseType,
                          monthlyRequiredEnergy:
                            parsedDataToSubmitForApproval.monthlyRequiredEnergy,
                          maxRequiredPower:
                            parsedDataToSubmitForApproval.maxRequiredPower,
                          numberOfUsers:
                            parsedDataToSubmitForApproval.estimationType ===
                            ESolarSolutionType.BOTH
                              ? parsedDataToSubmitForApproval.numberOfUsers
                              : null,
                          demandPerUser:
                            parsedDataToSubmitForApproval.estimationType ===
                            ESolarSolutionType.BOTH
                              ? parsedDataToSubmitForApproval.demandPerUser
                              : null,
                          nightLoad: parsedDataToSubmitForApproval.nightLoad,
                          sunlightHours:
                            parsedDataToSubmitForApproval.sunlightHours,
                          systemVoltage:
                            parsedDataToSubmitForApproval.systemVoltage,
                          autonomyDays:
                            parsedDataToSubmitForApproval.autonomyDays,
                          estimationSpaceClusterId: null,
                          estimationBasicScheduleId: persistRes.scheduleId,
                          isDraft: false,
                          countryCode: country,
                          installationCostRequested:
                            parsedDataToSubmitForApproval.includeOtherFees,
                        })
                          .unwrap()
                          .then(() => {
                            if (isPurchase === "true") {
                              triggerPurchaseSolarEstimation({
                                estimationId:
                                  parsedDataToSubmitForApproval.estimatorDetails
                                    .estimationId,
                              })
                                .unwrap()
                                .then(() => {
                                  localStorage.removeItem(
                                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                                  );
                                  localStorage.removeItem("simplifiedSave");

                                  localStorage.removeItem(
                                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                                  );
                                  localStorage.removeItem("directSave");

                                  localStorage.removeItem(
                                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                                  );
                                  showSuccessMessage(
                                    "System purchased successfully",
                                  );
                                })
                                .catch((error: IHttpError) => {
                                  if (
                                    error.status === 412 &&
                                    error.ovstErrorCode ===
                                      OvstErrorCode.OVST_CONS_0001
                                  ) {
                                    showErrorMessage(
                                      "Solar estimation is yet to be reviewed.",
                                    );
                                    return;
                                  }
                                  showErrorMessage("Failed to purchase system");
                                })
                                .finally(() => {
                                  localStorage.removeItem(
                                    "dataToSubmitForApproval",
                                  );
                                  localStorage.removeItem("isPurchase");

                                  const redirect =
                                    localStorage.getItem("redirect");
                                  if (redirect) {
                                    localStorage.removeItem("redirect");
                                    navigate(redirect);
                                    return;
                                  }

                                  navigate(AppRoute.ESTIMATIONS);
                                });
                              return;
                            }

                            submitForApproval({
                              estimationId: auth.token
                                ? parsedDataToSubmitForApproval.estimatorDetails
                                    .estimationId
                                : "",
                              estimatorToken: auth.token
                                ? ""
                                : parsedDataToSubmitForApproval.estimatorDetails
                                    .estimationToken,
                              includeInstallationCost:
                                parsedDataToSubmitForApproval.includeOtherFees,
                              address: parsedDataToSubmitForApproval.address,
                              note: parsedDataToSubmitForApproval.note,
                              longitude:
                                parsedDataToSubmitForApproval.longitude,
                              latitude: parsedDataToSubmitForApproval.latitude,
                            })
                              .unwrap()
                              .then(() => {
                                localStorage.removeItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                                );
                                localStorage.removeItem("simplifiedSave");

                                localStorage.removeItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                                );
                                localStorage.removeItem("directSave");

                                localStorage.removeItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                                );
                                localStorage.removeItem("waterHeaterSave");

                                showSuccessMessage(
                                  "Submitted for approval successfully",
                                );
                              })
                              .catch(() => {
                                showErrorMessage(
                                  "Failed to submit for approval",
                                );
                              })
                              .finally(() => {
                                localStorage.removeItem(
                                  "dataToSubmitForApproval",
                                );

                                const redirect =
                                  localStorage.getItem("redirect");
                                if (redirect) {
                                  localStorage.removeItem("redirect");
                                  navigate(redirect);
                                  return;
                                }

                                navigate(AppRoute.ESTIMATIONS);
                              });
                          })
                          .catch(() => {
                            showErrorMessage("Failed to submit for approval");

                            const redirect = localStorage.getItem("redirect");
                            if (redirect) {
                              localStorage.removeItem("redirect");
                              navigate(redirect);
                              return;
                            }

                            navigate(AppRoute.ESTIMATIONS);
                          });
                      })
                      .catch(() => {
                        showErrorMessage("Failed to submit for approval");
                        const redirect = localStorage.getItem("redirect");
                        if (redirect) {
                          localStorage.removeItem("redirect");
                          navigate(redirect);
                          return;
                        }

                        navigate(AppRoute.ESTIMATIONS);
                      });
                    break;
                  case EEstimationType.DIRECT:
                    triggerUpdateEstimation({
                      estimationId: auth.token
                        ? parsedDataToSubmitForApproval.estimatorDetails
                            .estimationId
                        : "",
                      estimatorToken: auth.token
                        ? ""
                        : parsedDataToSubmitForApproval.estimatorDetails
                            .estimationToken,
                      title: parsedDataToSubmitForApproval.title,
                      estimationType:
                        parsedDataToSubmitForApproval.estimationType,
                      applicationType:
                        parsedDataToSubmitForApproval.estimationType ===
                        ESolarSolutionType.BOTH
                          ? parsedDataToSubmitForApproval.applicationType
                          : null,
                      utilityOptionType:
                        parsedDataToSubmitForApproval.estimationType ===
                        ESolarSolutionType.BOTH
                          ? parsedDataToSubmitForApproval.utilityOptionType
                          : null,
                      solarEstimationType: EEstimationType.DIRECT,
                      systemType: parsedDataToSubmitForApproval.systemType,
                      inverterPhaseType:
                        parsedDataToSubmitForApproval.inverterPhaseType,
                      monthlyRequiredEnergy:
                        parsedDataToSubmitForApproval.monthlyRequiredEnergy,
                      maxRequiredPower:
                        parsedDataToSubmitForApproval.maxRequiredPower,
                      numberOfUsers:
                        parsedDataToSubmitForApproval.estimationType ===
                        ESolarSolutionType.BOTH
                          ? parsedDataToSubmitForApproval.numberOfUsers
                          : null,
                      demandPerUser:
                        parsedDataToSubmitForApproval.estimationType ===
                        ESolarSolutionType.BOTH
                          ? parsedDataToSubmitForApproval.demandPerUser
                          : null,
                      nightLoad: parsedDataToSubmitForApproval.nightLoad,
                      sunlightHours:
                        parsedDataToSubmitForApproval.sunlightHours,
                      systemVoltage:
                        parsedDataToSubmitForApproval.systemVoltage,
                      autonomyDays: parsedDataToSubmitForApproval.autonomyDays,
                      estimationSpaceClusterId:
                        parsedDataToSubmitForApproval.estimationSpaceClusterId,
                      estimationBasicScheduleId: null,
                      isDraft: false,
                      countryCode: country,
                      installationCostRequested:
                        parsedDataToSubmitForApproval.includeOtherFees,
                    })
                      .unwrap()
                      .then(() => {
                        if (isPurchase === "true") {
                          triggerPurchaseSolarEstimation({
                            estimationId:
                              parsedDataToSubmitForApproval.estimatorDetails
                                .estimationId,
                          })
                            .unwrap()
                            .then(() => {
                              localStorage.removeItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                              );
                              localStorage.removeItem("simplifiedSave");

                              localStorage.removeItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                              );
                              localStorage.removeItem("directSave");

                              localStorage.removeItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                              );
                              localStorage.removeItem("waterHeaterSave");
                              showSuccessMessage(
                                "System purchased successfully",
                              );
                            })
                            .catch((error: IHttpError) => {
                              if (
                                error.status === 412 &&
                                error.ovstErrorCode ===
                                  OvstErrorCode.OVST_CONS_0001
                              ) {
                                showErrorMessage(
                                  "Solar estimation is yet to be reviewed.",
                                );
                                return;
                              }
                              showErrorMessage("Failed to purchase system");
                            })
                            .finally(() => {
                              localStorage.removeItem(
                                "dataToSubmitForApproval",
                              );
                              localStorage.removeItem("isPurchase");

                              const redirect = localStorage.getItem("redirect");
                              if (redirect) {
                                localStorage.removeItem("redirect");
                                navigate(redirect);
                                return;
                              }

                              navigate(AppRoute.ESTIMATIONS);
                            });
                          return;
                        }

                        submitForApproval({
                          estimationId: auth.token
                            ? parsedDataToSubmitForApproval.estimatorDetails
                                .estimationId
                            : "",
                          estimatorToken: auth.token
                            ? ""
                            : parsedDataToSubmitForApproval.estimatorDetails
                                .estimationToken,
                          includeInstallationCost:
                            parsedDataToSubmitForApproval.includeOtherFees,
                          address: parsedDataToSubmitForApproval.address,
                          note: parsedDataToSubmitForApproval.note,
                          longitude: parsedDataToSubmitForApproval.longitude,
                          latitude: parsedDataToSubmitForApproval.latitude,
                        })
                          .unwrap()
                          .then(() => {
                            localStorage.removeItem(
                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                            );
                            localStorage.removeItem("simplifiedSave");

                            localStorage.removeItem(
                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                            );
                            localStorage.removeItem("directSave");

                            localStorage.removeItem(
                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                            );
                            localStorage.removeItem("waterHeaterSave");

                            showSuccessMessage(
                              "Submitted for approval successfully",
                            );
                          })
                          .catch(() => {
                            showErrorMessage("Failed to submit for approval");
                          })
                          .finally(() => {
                            localStorage.removeItem("dataToSubmitForApproval");

                            const redirect = localStorage.getItem("redirect");
                            if (redirect) {
                              localStorage.removeItem("redirect");
                              navigate(redirect);
                              return;
                            }

                            navigate(AppRoute.ESTIMATIONS);
                          });
                      })
                      .catch(() => {
                        showErrorMessage("Failed to submit for approval");
                        const redirect = localStorage.getItem("redirect");
                        if (redirect) {
                          localStorage.removeItem("redirect");
                          navigate(redirect);
                          return;
                        }

                        navigate(AppRoute.ESTIMATIONS);
                      });
                    break;
                  case EEstimationType.WATER_HEATER:
                    triggerUpdateEstimation({
                      estimationId: auth.token
                        ? parsedDataToSubmitForApproval.estimatorDetails
                            .estimationId
                        : "",
                      estimatorToken: auth.token
                        ? ""
                        : parsedDataToSubmitForApproval.estimatorDetails
                            .estimationToken,
                      title: parsedDataToSubmitForApproval.title,
                      estimationType:
                        parsedDataToSubmitForApproval.estimationType,
                      applicationType:
                        parsedDataToSubmitForApproval.applicationType,
                      utilityOptionType:
                        parsedDataToSubmitForApproval.utilityOptionType,
                      solarEstimationType: null,
                      systemType: null,
                      inverterPhaseType: null,
                      monthlyRequiredEnergy: null,
                      maxRequiredPower: null,
                      numberOfUsers:
                        parsedDataToSubmitForApproval.numberOfUsers,
                      demandPerUser:
                        parsedDataToSubmitForApproval.demandPerUser,
                      nightLoad: null,
                      sunlightHours: null,
                      systemVoltage: null,
                      autonomyDays: null,
                      estimationSpaceClusterId: null,
                      estimationBasicScheduleId: null,
                      isDraft: false,
                      countryCode: country,
                      installationCostRequested:
                        parsedDataToSubmitForApproval.includeOtherFees,
                    })
                      .unwrap()
                      .then(() => {
                        if (isPurchase === "true") {
                          triggerPurchaseSolarEstimation({
                            estimationId:
                              parsedDataToSubmitForApproval.estimatorDetails
                                .estimationId,
                          })
                            .unwrap()
                            .then(() => {
                              localStorage.removeItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                              );
                              localStorage.removeItem("waterHeaterSave");

                              localStorage.removeItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                              );
                              localStorage.removeItem("simplifiedSave");

                              localStorage.removeItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                              );
                              localStorage.removeItem("directSave");
                              showSuccessMessage(
                                "System purchased successfully",
                              );
                            })
                            .catch((error: IHttpError) => {
                              if (
                                error.status === 412 &&
                                error.ovstErrorCode ===
                                  OvstErrorCode.OVST_CONS_0001
                              ) {
                                showErrorMessage(
                                  "Solar estimation is yet to be reviewed.",
                                );
                                return;
                              }
                              showErrorMessage("Failed to purchase system");
                            })
                            .finally(() => {
                              localStorage.removeItem(
                                "dataToSubmitForApproval",
                              );
                              localStorage.removeItem("isPurchase");

                              const redirect = localStorage.getItem("redirect");
                              if (redirect) {
                                localStorage.removeItem("redirect");
                                navigate(redirect);
                                return;
                              }

                              navigate(AppRoute.ESTIMATIONS);
                            });
                          return;
                        }

                        submitForApproval({
                          estimationId: auth.token
                            ? parsedDataToSubmitForApproval.estimatorDetails
                                .estimationId
                            : "",
                          estimatorToken: auth.token
                            ? ""
                            : parsedDataToSubmitForApproval.estimatorDetails
                                .estimationToken,
                          includeInstallationCost:
                            parsedDataToSubmitForApproval.includeOtherFees,
                          address: parsedDataToSubmitForApproval.address,
                          note: parsedDataToSubmitForApproval.note,
                          longitude: parsedDataToSubmitForApproval.longitude,
                          latitude: parsedDataToSubmitForApproval.latitude,
                        })
                          .unwrap()
                          .then(() => {
                            localStorage.removeItem(
                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                            );
                            localStorage.removeItem("waterHeaterSave");

                            localStorage.removeItem(
                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                            );
                            localStorage.removeItem("simplifiedSave");

                            localStorage.removeItem(
                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                            );
                            localStorage.removeItem("directSave");

                            showSuccessMessage(
                              "Submitted for approval successfully",
                            );
                          })
                          .catch(() => {
                            showErrorMessage("Failed to submit for approval");
                          })
                          .finally(() => {
                            localStorage.removeItem("dataToSubmitForApproval");

                            const redirect = localStorage.getItem("redirect");
                            if (redirect) {
                              localStorage.removeItem("redirect");
                              navigate(redirect);
                              return;
                            }

                            navigate(AppRoute.ESTIMATIONS);
                          });
                      })
                      .catch(() => {
                        showErrorMessage("Failed to submit for approval");
                        const redirect = localStorage.getItem("redirect");
                        if (redirect) {
                          localStorage.removeItem("redirect");
                          navigate(redirect);
                          return;
                        }

                        navigate(AppRoute.ESTIMATIONS);
                      });
                    break;
                  default:
                    break;
                }

                return;
              }

              const redirect = localStorage.getItem("redirect");
              if (redirect) {
                localStorage.removeItem("redirect");
                navigate(redirect);
                return;
              }
              navigate(AppRoute.ESTIMATIONS);
            });
        }
      })
      .catch((error) => {
        if (error.status === 412) {
          if (
            error.ovstErrorCode === OvstErrorCode.OVST_SEC_0005 ||
            error.ovstErrorCode === OvstErrorCode.OVST_CONS_0020 ||
            error.ovstErrorCode === OvstErrorCode.OVST_CONS_0027
          ) {
            setIsExpired(true);
            setIsValid(true);
          } else if (
            error.ovstErrorCode === OvstErrorCode.OVST_0001 ||
            error.ovstErrorCode === OvstErrorCode.OVST_APP_SEC_0004
          ) {
            setIsExpired(false);
            setIsValid(false);
          }
        } else {
          showErrorMessage(error.message || "Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (otp.length < 1) {
      setIsValid(true);
    }
  }, [otp]);

  return (
    <>
      <Row className="justify-content-center">
        <Col className="mt-5">
          <OtpInput
            value={otp}
            onChange={handleChange}
            numInputs={6}
            isInputNum={true}
            placeholder={"------"}
            inputStyle={{
              width: "52px",
              height: "70px",
              margin: "0 10px",
              fontSize: "36px",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "var(--color-app-select-border)",
              borderRadius: "8px",
              outline: "none",
              boxShadow: "none",
              color: "var(--color-app-select-input-text)",
              backgroundColor: "var(--color-app-select-input)",
            }}
            className={styles["input-field"]}
            focusStyle={{
              borderColor: "rgb(0, 123, 255)",
              outline: "none",
            }}
            errorStyle={{
              borderColor: "#D74242",
              outline: "none",
            }}
            hasErrored={!isValid}
            containerStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        </Col>
      </Row>
      <Row>
        {!isValid && (
          <Col className="text-danger justify-content-center font-size-14 mt-2 text-center">
            The code you entered is incorrect. Please try again or request a new
            code.
          </Col>
        )}
        {isExpired && (
          <Col className="text-danger justify-content-center font-size-14 mt-2 text-center">
            The code you entered is expired. Please request a new code.
          </Col>
        )}
      </Row>
      <Row className="justify-content-center">
        <Col md={10}>
          <AppButton
            text="Verify"
            className={`mt-5`}
            variant="blue"
            isLoading={loadingGetOTP}
            onClick={onSubmit}
            disabled={enteredOtp}
          />
        </Col>
      </Row>
      <Row className="justify-content-center mt-4">
        <EmailOtpVerificationFooter
          whereTo={whereTo}
          email={email}
          handleOTP={handleChange}
          resetError={resetError}
          setIsExpired={() => setIsExpired(false)}
        />
      </Row>
      <SpinnerModal
        show={
          isGetProfileFetching ||
          isLoadingPersistEstimation ||
          isSubmittingForApproval ||
          isLoadingUpdateEstimation ||
          isLoadingPurchaseSolarEstimation
        }
      />
    </>
  );
};

export default EmailOtpVerificationForm;
